import React from "react";
import { DocumentMyDeskResponse } from "apis/data-contracts";
import DashboardStastic from "components/dashboard/DashboardStastic";
import {
  ContentsBoxStyle,
  IconStyle,
  ValueStyle,
} from "components/dashboard/DashboardStastic/styles";
import useDocumentStatusCountQuery from "features/document/hooks/queries/useDocumentStatusCountQuery";
import styled, { css, useTheme } from "styled-components";
import {
  myDeskStatusSummaryItems,
  MyDeskStatusFilterValue,
} from "./myDeskStatus";

interface MyDeskStatusSummaryProps {
  categoryUuid: string;
  selectedStatus?: DocumentMyDeskResponse["status"];
  onSelectStatus: (status: MyDeskStatusFilterValue) => void;
  className?: string;
}

const MyDeskStatusSummary = ({
  categoryUuid,
  selectedStatus,
  onSelectStatus,
  className,
}: MyDeskStatusSummaryProps) => {
  const theme = useTheme();
  const { data } = useDocumentStatusCountQuery({
    categoryUuid,
  });

  return (
    <StatusSummaryList className={className}>
      {myDeskStatusSummaryItems.map((item) => {
        const Icon = item.icon;
        const count = data?.documentStatusCount[item.countKey] || 0;

        return (
          <StatusButton
            key={item.value}
            type="button"
            $active={selectedStatus === item.value}
            onClick={() => onSelectStatus(item.value)}
          >
            <MyDeskDashboardStatistic
              label={item.label}
              icon={<Icon />}
              iconBgColor={getIconBgColor(item.tone, theme)}
            >
              ({count})
            </MyDeskDashboardStatistic>
          </StatusButton>
        );
      })}
    </StatusSummaryList>
  );
};

export default MyDeskStatusSummary;

const getIconBgColor = (
  tone: (typeof myDeskStatusSummaryItems)[number]["tone"],
  theme: ReturnType<typeof useTheme>,
) => {
  switch (tone) {
    case "pending":
      return theme.color.brick[300];
    case "approved":
      return theme.color.brick[500];
    case "supplement":
      return theme.color.brick[400];
    case "temp":
      return theme.color.grey[500];
    default:
      return theme.color.grey[500];
  }
};

const StatusSummaryList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2.4rem;

  ${({ theme }) => css`
    ${theme.media.small} {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1.6rem 1.2rem;
    }
  `}
`;

const MyDeskDashboardStatistic = styled(DashboardStastic)`
  ${IconStyle} {
    width: 2.911rem;
    height: 2.911rem;
    border-radius: 0.606rem;

    svg {
      width: 1.456rem;
      height: 1.456rem;
    }
  }

  ${ContentsBoxStyle} {
    flex-direction: row;
    align-items: center;
    gap: 0.4rem;
  }

  ${ValueStyle} {
    display: inline-flex;
    justify-content: flex-start;
    width: 7ch;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    font-variant-numeric: tabular-nums;
  }
`;

const StatusButton = styled.button<{ $active: boolean }>`
  display: flex;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;

  opacity: ${({ $active }) => ($active ? 1 : 0.88)};

  ${({ $active, theme }) =>
    $active &&
    css`
      border-radius: 1rem;
      box-shadow: inset 0 0 0 0.1rem ${theme.color.stroke.light};
    `}
`;
